import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../context/auth.store';

// Clean text for speech
function cleanForAudio(text) {
  if (!text) return '';
  return text
    .replace(/###\s+/g, '')
    .replace(/##\s+/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/>\s+/g, '')
    .replace(/[-*]\s+/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function KyroGlobalAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [query, setQuery] = useState('');
  const [interimText, setInterimText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [proactiveBriefing, setProactiveBriefing] = useState(null);
  const [showProactiveBubble, setShowProactiveBubble] = useState(false);
  const [continuousMode, setContinuousMode] = useState(true);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTicketsTable, setShowTicketsTable] = useState(false);

  const recognitionRef = useRef(null);
  const panelRef = useRef(null);
  const spokenTextRef = useRef('');
  const hasSubmittedRef = useRef(false);
  const autoSubmitTimerRef = useRef(null);
  const followUpTimerRef = useRef(null);

  const activeUtteranceRef = useRef(null);

  // Pre-load speech synthesis voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
      window.speechSynthesis.getVoices();
    }
  }, []);

  // Unlock audio subsystem on user gesture
  useEffect(() => {
    const unlock = () => {
      try {
        if ('speechSynthesis' in window && window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
      } catch (e) {}
    };
    window.addEventListener('click', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchstart', unlock);
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // External trigger for contextual drilldowns (e.g. from ReportViewerPage)
  useEffect(() => {
    const handleContextQuery = (e) => {
      if (e.detail?.query) {
        setIsOpen(true);
        setIsExpanded(true);
        handleSendQuery(e.detail.query);
      }
    };
    window.addEventListener('kyro-open-with-query', handleContextQuery);
    return () => window.removeEventListener('kyro-open-with-query', handleContextQuery);
  }, []);

  // Chrome Mac 8s watchdog to prevent long utterance freezing
  useEffect(() => {
    let interval = null;
    if (speaking) {
      interval = setInterval(() => {
        if (window.speechSynthesis?.speaking) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }
      }, 7500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [speaking]);

  // Helper to play subtle futuristic chime and wake up browser audio pipeline
  const playAudioChime = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const now = ctx.currentTime;
      osc.frequency.setValueAtTime(587.33, now); // D5
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.08); // A5
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
      osc.start(now);
      osc.stop(now + 0.3);
    } catch (e) {}
  };

  // Speak voice summary using Web Speech API (Always responds in audio)
  const speakText = (text) => {
    if (!('speechSynthesis' in window) || !text) {
      console.warn('[KYRO Voice]: SpeechSynthesis not supported or empty text');
      return;
    }

    // Wake audio subsystem with a brief pleasant tone
    playAudioChime();

    // 1. Cancel previous speech if active
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }

    const cleanSpeech = cleanForAudio(text) || text;
    if (!cleanSpeech) return;

    // 2. CRUCIAL FIX FOR CHROME/EDGE: Give audio thread 65ms to finish cancel before speak()
    setTimeout(() => {
      try {
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }

        const utterance = new SpeechSynthesisUtterance(cleanSpeech);
        // CRITICAL BUG FIX: Hold persistent reference so V8 GC does not kill the utterance!
        activeUtteranceRef.current = utterance;
        window.__kyroActiveUtterance = utterance;

        utterance.lang = 'en-US';
        utterance.volume = 1.0;
        utterance.rate = 1.05;
        utterance.pitch = 0.95;

        const voices = window.speechSynthesis.getVoices();
        if (voices && voices.length > 0) {
          const kyroVoice =
            voices.find(
              (v) =>
                v.lang.startsWith('en') &&
                (v.name.includes('Daniel') ||
                  v.name.includes('UK English') ||
                  v.name.includes('Male') ||
                  v.name.includes('Natural') ||
                  v.name.includes('Google UK English Male') ||
                  v.name.includes('Alex'))
            ) || voices.find((v) => v.lang.startsWith('en'));

          if (kyroVoice) utterance.voice = kyroVoice;
        }

        utterance.onstart = () => {
          setSpeaking(true);
          setIsFollowUp(false);
        };

        utterance.onend = () => {
          setSpeaking(false);
          activeUtteranceRef.current = null;
          window.__kyroActiveUtterance = null;
          // Continuous follow-up hands-free dialogue like Alexa
          if (continuousMode && speechSupported) {
            setTimeout(() => {
              startFollowUpListening();
            }, 350);
          }
        };

        utterance.onerror = (e) => {
          console.warn('[KYRO Speech Error]:', e);
          setSpeaking(false);
          setIsFollowUp(false);
          activeUtteranceRef.current = null;
          window.__kyroActiveUtterance = null;
        };

        window.speechSynthesis.resume?.();
        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.error('[KYRO speakText exception]:', err);
        setSpeaking(false);
      }
    }, 65);
  };

  const stopSpeaking = () => {
    activeUtteranceRef.current = null;
    window.__kyroActiveUtterance = null;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  // Proactive Briefing on mount (works on Login page and inside protected pages)
  useEffect(() => {
    api.get('/ai/proactive-briefing')
      .then((res) => {
        setProactiveBriefing(res.data);
        setTimeout(() => {
          setShowProactiveBubble(true);
        }, 1800);
      })
      .catch((err) => console.warn('[KYRO Briefing]:', err.message));
  }, [user]);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          setIsListening(true);
          hasSubmittedRef.current = false;
          spokenTextRef.current = '';
        };

        recognition.onresult = (event) => {
          if (followUpTimerRef.current) clearTimeout(followUpTimerRef.current);
          let final = '';
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              final += event.results[i][0].transcript;
            } else {
              interim += event.results[i][0].transcript;
            }
          }
          const current = (final || interim).trim();
          if (current) {
            spokenTextRef.current = current;
          }

          if (final && final.trim().length > 0) {
            setQuery(final);
            setInterimText('');
            // Auto submit on final speech with 500ms debounce (Alexa-style hands-free)
            if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
            autoSubmitTimerRef.current = setTimeout(() => {
              if (!hasSubmittedRef.current) {
                hasSubmittedRef.current = true;
                try {
                  recognitionRef.current?.stop();
                } catch (e) {}
                setIsListening(false);
                setIsFollowUp(false);
                handleAsk(final.trim());
              }
            }, 500);
          } else {
            setInterimText(interim);
          }
        };

        recognition.onerror = (event) => {
          console.warn('[Global KYRO Voice Error]:', event.error);
          setIsListening(false);
          setIsFollowUp(false);
          setInterimText('');
          if (event.error === 'not-allowed') {
            toast.error('Microphone access blocked. Please permit mic in browser settings.');
          }
        };

        recognition.onend = () => {
          setIsListening(false);
          setIsFollowUp(false);
          // Auto-submit fallback on speech end if not already submitted
          if (!hasSubmittedRef.current && spokenTextRef.current && spokenTextRef.current.trim().length > 0) {
            hasSubmittedRef.current = true;
            const textToSubmit = spokenTextRef.current.trim();
            spokenTextRef.current = '';
            setQuery(textToSubmit);
            setInterimText('');
            handleAsk(textToSubmit);
          }
        };

        recognitionRef.current = recognition;
      } catch (e) {
        console.warn('SpeechRecognition init error:', e);
      }
    }
  }, []);

  // Stop speaking when route changes
  useEffect(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setIsFollowUp(false);
    if (followUpTimerRef.current) clearTimeout(followUpTimerRef.current);
  }, [location.pathname]);

  // Start follow-up listening mode after speaking
  const startFollowUpListening = () => {
    if (!speechSupported || isListening) return;
    setIsFollowUp(true);
    hasSubmittedRef.current = false;
    spokenTextRef.current = '';
    setInterimText('');

    try {
      recognitionRef.current?.start();
      setIsListening(true);

      // Auto-expire follow-up window after 6.5s of silence
      if (followUpTimerRef.current) clearTimeout(followUpTimerRef.current);
      followUpTimerRef.current = setTimeout(() => {
        if (!hasSubmittedRef.current && (!spokenTextRef.current || !spokenTextRef.current.trim())) {
          try {
            recognitionRef.current?.stop();
          } catch (e) {}
          setIsListening(false);
          setIsFollowUp(false);
        }
      }, 6500);
    } catch (err) {
      console.warn('Follow-up start error:', err);
      setIsListening(false);
      setIsFollowUp(false);
    }
  };

  const toggleVoice = () => {
    setIsOpen(true);
    if (!speechSupported) {
      toast.error('Voice input is not supported in this browser. Please use Google Chrome or Microsoft Edge.');
      return;
    }

    if (isListening) {
      if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);
      try {
        recognitionRef.current?.stop();
      } catch (e) {}
      setIsListening(false);
      setIsFollowUp(false);
      if (spokenTextRef.current && spokenTextRef.current.trim().length > 0 && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true;
        const textToSubmit = spokenTextRef.current.trim();
        spokenTextRef.current = '';
        setQuery(textToSubmit);
        setInterimText('');
        handleAsk(textToSubmit);
      }
    } else {
      stopSpeaking();
      spokenTextRef.current = '';
      hasSubmittedRef.current = false;
      setInterimText('');
      setQuery('');
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        setIsFollowUp(false);
        toast('🎙️ KYRO is listening... Speak your question now', { icon: '🎙️' });
      } catch (err) {
        console.warn(err);
        setIsListening(false);
      }
    }
  };

  const handleAsk = async (explicitText) => {
    const text = (explicitText || query).trim();
    if (!text || processing) return;

    hasSubmittedRef.current = true;
    if (autoSubmitTimerRef.current) clearTimeout(autoSubmitTimerRef.current);

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch (e) {}
      setIsListening(false);
      setInterimText('');
    }

    setProcessing(true);
    setQuery(text);
    stopSpeaking();

    try {
      const res = await api.post('/ai/chat', { question: text });
      setLastResponse(res.data);
      if (res.data.drilldownData) {
        setIsExpanded(true);
      }

      // 1. Spoken audio response (Alexa-style) - Responds in audio automatically
      const spokenSummary = res.data.voiceSummary || res.data.answer.split('\n')[0] || 'I have completed your request.';
      speakText(spokenSummary);

      // 2. Voice Login Action Dispatch (Works on login page)
      if (res.data.action && res.data.action.startsWith('login_')) {
        window.dispatchEvent(new CustomEvent('kyro-voice-login', { detail: { action: res.data.action } }));
      }

      // 3. Automated Navigation if requested (e.g. "Take me to central reports")
      if (res.data.navigateTo && res.data.navigateTo !== location.pathname) {
        setTimeout(() => {
          navigate(res.data.navigateTo);
        }, 1200);
      }
    } catch (err) {
      console.error(err);
      toast.error('KYRO could not process request.');
    } finally {
      setProcessing(false);
    }
  };

  // If on the dedicated AI Assistant page, hide the floating orb to avoid redundancy
  if (location.pathname === '/ai-assistant') {
    return null;
  }

  return (
    <>
      {/* ── Floating Holographic Assistant Trigger (Bottom Right) ── */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '26px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '12px',
        }}
      >
        {/* Floating Assistant Card Popup */}
        {isOpen && (
          <div
            ref={panelRef}
            style={{
              width: isExpanded ? '640px' : '385px',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: isExpanded ? '86vh' : '75vh',
              background: 'linear-gradient(145deg, #0B132B 0%, #151F38 100%)',
              border: '1.5px solid rgba(0, 192, 250, 0.4)',
              borderRadius: '20px',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 192, 250, 0.2)',
              color: 'white',
              padding: '18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              animation: 'fadeIn 0.2s ease-out',
              overflowY: 'auto',
              transition: 'width 0.25s ease, max-height 0.25s ease',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '8px',
                    background: 'radial-gradient(circle, #00C0FA 0%, #0052CC 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 10px rgba(0, 192, 250, 0.6)',
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
                  </svg>
                </span>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: '900', fontSize: '14px', letterSpacing: '0.04em' }}>
                    KYRO <span style={{ color: '#00C0FA', fontSize: '11px', fontWeight: '700' }}>// {isExpanded ? 'OPERATIONAL CO-PILOT' : 'VOICE ASSISTANT'}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: '#05CD99', fontWeight: '700' }}>
                    ● 100% Offline · {isExpanded ? 'Deep Analytical Panel' : 'Alexa-Style Mode'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {speaking && (
                  <button
                    onClick={stopSpeaking}
                    style={{
                      background: 'rgba(238, 93, 80, 0.2)',
                      border: '1px solid #EE5D50',
                      color: '#EE5D50',
                      borderRadius: '6px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      fontWeight: '700',
                      cursor: 'pointer',
                    }}
                    title="Stop speaking"
                  >
                    ⏹️ Silence
                  </button>
                )}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{
                    background: isExpanded ? 'rgba(0, 192, 250, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                    border: isExpanded ? '1px solid #00C0FA' : 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    color: isExpanded ? '#00C0FA' : '#94A3B8',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={isExpanded ? 'Compact View' : 'Expand Analytical Drawer'}
                >
                  {isExpanded ? '🗗' : '⛶'}
                </button>
                <button
                  onClick={() => {
                    stopSpeaking();
                    setIsOpen(false);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    color: '#94A3B8',
                    cursor: 'pointer',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Speaking Audio Indicator */}
            {speaking && (
              <div
                style={{
                  background: 'rgba(0, 192, 250, 0.15)',
                  border: '1px solid rgba(0, 192, 250, 0.4)',
                  padding: '6px 12px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '11.5px',
                  color: '#00C0FA',
                  fontWeight: '700',
                }}
              >
                <span>🔊 KYRO Speaking Audio Response...</span>
                <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                  <span style={{ height: '10px', width: '3px', background: '#00C0FA', borderRadius: '2px' }} />
                  <span style={{ height: '16px', width: '3px', background: '#00C0FA', borderRadius: '2px' }} />
                  <span style={{ height: '8px', width: '3px', background: '#00C0FA', borderRadius: '2px' }} />
                  <span style={{ height: '14px', width: '3px', background: '#00C0FA', borderRadius: '2px' }} />
                </div>
              </div>
            )}

            {/* Live Interim Speech Transcription or Follow-up */}
            {isListening && (
              <div
                style={{
                  background: isFollowUp ? 'rgba(5, 205, 153, 0.12)' : 'rgba(238, 93, 80, 0.12)',
                  border: isFollowUp ? '1px dashed #05CD99' : '1px dashed #EE5D50',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  color: isFollowUp ? '#6EE7B7' : '#FCA5A5',
                }}
              >
                <div style={{ fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: isFollowUp ? '#05CD99' : '#EE5D50',
                      animation: 'pulse 1s infinite',
                    }}
                  />
                  <span>
                    {isFollowUp
                      ? '🎙️ Follow-up listening... speak anytime (auto-submits on pause)'
                      : '🎙️ Listening... speak now (auto-submits on pause)'}
                  </span>
                </div>
                <div style={{ fontStyle: 'italic', color: '#FFFFFF', minHeight: '18px' }}>
                  "{interimText || query || 'Listening for your voice...'}"
                </div>
              </div>
            )}

            {/* Assistant Response Card */}
            {lastResponse && !isListening && (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '12px 14px',
                  fontSize: '12.5px',
                  maxHeight: isExpanded ? '520px' : '260px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {lastResponse.voiceSummary && (
                  <div
                    style={{
                      fontSize: '12px',
                      lineHeight: '1.5',
                      color: '#E2E8F0',
                      borderLeft: '2px solid #00C0FA',
                      paddingLeft: '8px',
                    }}
                  >
                    "{lastResponse.voiceSummary}"
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>
                  <span>⚡ {lastResponse.latencyMs || 45}ms</span>
                  <span>{lastResponse.sources?.length || 0} cited files</span>
                  <span style={{ color: '#05CD99' }}>100% Offline</span>
                  {lastResponse.drilldownData && (
                    <span style={{ color: '#00C0FA', fontWeight: '700' }}>● Multi-Tier Analytics Active</span>
                  )}
                </div>

                {/* ── Interactive Deep Drilldown Module ── */}
                {lastResponse.drilldownData && (
                  <div
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(0, 192, 250, 0.25)',
                      borderRadius: '12px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    {/* Header info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: '800', color: '#00C0FA', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📊</span>
                        <span>{lastResponse.drilldownData.reportName}</span>
                      </div>
                      <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                        Sheet: <code style={{ color: '#F8FAFC' }}>{lastResponse.drilldownData.sheetName}</code>
                      </span>
                    </div>

                    {/* KPI 4-Grid */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isExpanded ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
                        gap: '8px',
                      }}
                    >
                      <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '8px', borderRadius: '8px', borderLeft: '3px solid #00C0FA' }}>
                        <div style={{ fontSize: '9.5px', color: '#94A3B8', fontWeight: '700' }}>TOTAL TICKETS</div>
                        <div style={{ fontSize: '16px', fontWeight: '900', color: '#F8FAFC', marginTop: '2px' }}>
                          {lastResponse.drilldownData.kpis?.totalRecords?.toLocaleString() || 0}
                        </div>
                      </div>

                      <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '8px', borderRadius: '8px', borderLeft: '3px solid #05CD99' }}>
                        <div style={{ fontSize: '9.5px', color: '#94A3B8', fontWeight: '700' }}>RESOLUTION RATE</div>
                        <div style={{ fontSize: '16px', fontWeight: '900', color: '#05CD99', marginTop: '2px' }}>
                          {lastResponse.drilldownData.kpis?.resolutionRate || 0}%
                        </div>
                        <div style={{ fontSize: '9px', color: '#94A3B8' }}>
                          {lastResponse.drilldownData.kpis?.closedCount} closed
                        </div>
                      </div>

                      <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '8px', borderRadius: '8px', borderLeft: '3px solid #F59E0B' }}>
                        <div style={{ fontSize: '9.5px', color: '#94A3B8', fontWeight: '700' }}>AVG TURNAROUND</div>
                        <div style={{ fontSize: '16px', fontWeight: '900', color: '#F59E0B', marginTop: '2px' }}>
                          {lastResponse.drilldownData.kpis?.averageTat || 'N/A'} <span style={{ fontSize: '10px' }}>days</span>
                        </div>
                      </div>

                      <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '8px', borderRadius: '8px', borderLeft: '3px solid #EF4444' }}>
                        <div style={{ fontSize: '9.5px', color: '#94A3B8', fontWeight: '700' }}>CRITICAL OVERDUE</div>
                        <div style={{ fontSize: '16px', fontWeight: '900', color: '#EF4444', marginTop: '2px' }}>
                          {lastResponse.drilldownData.kpis?.criticalOverdueCount || 0}
                        </div>
                        <div style={{ fontSize: '9px', color: '#EF4444' }}>>14d SLA breach</div>
                      </div>
                    </div>

                    {/* Aging Buckets Stacked Visualizer */}
                    {lastResponse.drilldownData.agingBuckets && (
                      <div style={{ marginTop: '2px' }}>
                        <div style={{ fontSize: '10.5px', fontWeight: '700', color: '#CBD5E1', marginBottom: '4px' }}>
                          AGING DISTRIBUTION (PENDING INVENTORY):
                        </div>
                        <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.1)' }}>
                          <div style={{ width: `${lastResponse.drilldownData.agingBuckets.fresh.percentage}%`, background: '#05CD99' }} title={`0-3 Days: ${lastResponse.drilldownData.agingBuckets.fresh.count}`} />
                          <div style={{ width: `${lastResponse.drilldownData.agingBuckets.normal.percentage}%`, background: '#38BDF8' }} title={`4-7 Days: ${lastResponse.drilldownData.agingBuckets.normal.count}`} />
                          <div style={{ width: `${lastResponse.drilldownData.agingBuckets.warning.percentage}%`, background: '#F59E0B' }} title={`8-14 Days: ${lastResponse.drilldownData.agingBuckets.warning.count}`} />
                          <div style={{ width: `${lastResponse.drilldownData.agingBuckets.critical.percentage}%`, background: '#EF4444' }} title={`>14 Days: ${lastResponse.drilldownData.agingBuckets.critical.count}`} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5px', color: '#94A3B8', marginTop: '4px' }}>
                          <span style={{ color: '#05CD99' }}>● 0-3d ({lastResponse.drilldownData.agingBuckets.fresh.count})</span>
                          <span style={{ color: '#38BDF8' }}>● 4-7d ({lastResponse.drilldownData.agingBuckets.normal.count})</span>
                          <span style={{ color: '#F59E0B' }}>● 8-14d ({lastResponse.drilldownData.agingBuckets.warning.count})</span>
                          <span style={{ color: '#EF4444' }}>● >14d ({lastResponse.drilldownData.agingBuckets.critical.count})</span>
                        </div>
                      </div>
                    )}

                    {/* Top Branches Bar Breakdown */}
                    {lastResponse.drilldownData.topBranches?.length > 0 && (
                      <div style={{ marginTop: '2px' }}>
                        <div style={{ fontSize: '10.5px', fontWeight: '700', color: '#CBD5E1', marginBottom: '4px' }}>
                          TOP SERVICE CENTERS:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {lastResponse.drilldownData.topBranches.slice(0, 3).map((b, bIdx) => (
                            <div key={bIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                              <span style={{ width: '85px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#E2E8F0' }}>
                                {b.name}
                              </span>
                              <div style={{ flex: 1, height: '6px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ width: `${b.percentage}%`, height: '100%', background: 'linear-gradient(90deg, #00C0FA, #0052CC)' }} />
                              </div>
                              <span style={{ width: '50px', textAlign: 'right', color: '#94A3B8', fontSize: '9.5px' }}>
                                {b.count} ({b.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Row Level Ticket Inspector Toggle */}
                    {lastResponse.drilldownData.sampleTickets?.length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <button
                          onClick={() => setShowTicketsTable(!showTicketsTable)}
                          style={{
                            background: 'rgba(0, 192, 250, 0.1)',
                            border: '1px solid rgba(0, 192, 250, 0.3)',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            color: '#38BDF8',
                            fontSize: '10.5px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {showTicketsTable ? '▼ Hide Ticket Rows' : '▶ Inspect Normalized Tickets (Level 4)'}
                        </button>

                        {showTicketsTable && (
                          <div
                            style={{
                              marginTop: '6px',
                              maxHeight: '140px',
                              overflowY: 'auto',
                              border: '1px solid rgba(255, 255, 255, 0.08)',
                              borderRadius: '6px',
                              fontSize: '9.5px',
                            }}
                          >
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                              <thead>
                                <tr style={{ background: 'rgba(255, 255, 255, 0.08)', color: '#94A3B8' }}>
                                  <th style={{ padding: '3px 6px' }}>RMA #</th>
                                  <th style={{ padding: '3px 6px' }}>Branch</th>
                                  <th style={{ padding: '3px 6px' }}>Status</th>
                                  <th style={{ padding: '3px 6px' }}>TAT</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lastResponse.drilldownData.sampleTickets.slice(0, 10).map((t, tIdx) => (
                                  <tr key={tIdx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                                    <td style={{ padding: '3px 6px', color: '#F8FAFC', fontWeight: '600' }}>{t.rma}</td>
                                    <td style={{ padding: '3px 6px', color: '#94A3B8' }}>{t.branch}</td>
                                    <td style={{ padding: '3px 6px', color: t.status.toLowerCase().includes('closed') ? '#05CD99' : '#F59E0B' }}>
                                      {t.status}
                                    </td>
                                    <td style={{ padding: '3px 6px', color: t.isOverdue ? '#EF4444' : '#94A3B8' }}>
                                      {t.tatDays}d {t.isOverdue && '⚠️'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {lastResponse.sources?.length > 0 && (
                  <div style={{ marginTop: '2px' }}>
                    <div style={{ fontSize: '10.5px', color: '#94A3B8', marginBottom: '4px' }}>CITATIONS:</div>
                    {lastResponse.sources.slice(0, 3).map((s, idx) => (
                      <div
                        key={idx}
                        style={{
                          fontSize: '11px',
                          color: '#38BDF8',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setIsOpen(false);
                          navigate(`/ai-assistant?inspect=${s.fileId}`);
                        }}
                        title={s.name}
                      >
                        📄 {s.name}
                      </div>
                    ))}
                  </div>
                )}

                {lastResponse.navigateTo && (
                  <button
                    onClick={() => navigate(lastResponse.navigateTo)}
                    style={{
                      marginTop: '4px',
                      background: 'linear-gradient(135deg, #00C0FA 0%, #0088CC 100%)',
                      color: '#0B132B',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      alignSelf: 'flex-start',
                    }}
                  >
                    Open Live Report ({lastResponse.navigateTo}) &rarr;
                  </button>
                )}
              </div>
            )}

            {/* Quick Suggestions & Continuous Dialogue Mode Toggle */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
              <button
                onClick={() => handleAsk('What is my daily briefing?')}
                disabled={processing}
                style={{
                  background: 'rgba(0, 192, 250, 0.15)',
                  border: '1px solid rgba(0, 192, 250, 0.4)',
                  borderRadius: '8px',
                  color: '#00C0FA',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span>⚡ Daily Briefing</span>
              </button>

              <button
                onClick={() => handleAsk(user ? 'What reports are mapped to me?' : 'What is PX-Reports?')}
                disabled={processing}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#CBD5E1',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '4px 8px',
                  cursor: 'pointer',
                }}
              >
                {user ? '📧 My Reports' : '📊 About Platform'}
              </button>

              <button
                onClick={() => handleAsk('How is the day?')}
                disabled={processing}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#CBD5E1',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '4px 8px',
                  cursor: 'pointer',
                }}
              >
                ☀️ How's the day?
              </button>

              <button
                onClick={() => handleAsk('How is the weather?')}
                disabled={processing}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#CBD5E1',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '4px 8px',
                  cursor: 'pointer',
                }}
              >
                🌤️ Weather
              </button>

              <button
                onClick={() => {
                  speakText('Voice audio output is active. I am KYRO, ready for your commands.');
                  toast.success('Playing KYRO audio test...');
                }}
                style={{
                  background: 'rgba(255, 107, 0, 0.15)',
                  border: '1px solid rgba(255, 107, 0, 0.4)',
                  borderRadius: '8px',
                  color: '#FF6B00',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Test KYRO audio playback"
              >
                <span>🔊 Test Audio</span>
              </button>

              <button
                onClick={() => setContinuousMode(!continuousMode)}
                style={{
                  marginLeft: 'auto',
                  background: continuousMode ? 'rgba(5, 205, 153, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                  border: continuousMode ? '1px solid #05CD99' : '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: continuousMode ? '#05CD99' : '#94A3B8',
                  fontSize: '10.5px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Continuous Mode allows ongoing hands-free back-and-forth dialogue"
              >
                <span>{continuousMode ? '🎙️ Follow-up: ON' : '🎙️ Follow-up: OFF'}</span>
              </button>
            </div>

            {/* Quick Actions / Link to full audit */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
              <button
                onClick={() => {
                  stopSpeaking();
                  setIsOpen(false);
                  navigate('/ai-assistant');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#00C0FA',
                  cursor: 'pointer',
                  fontWeight: '700',
                  padding: 0,
                  fontSize: '11px',
                }}
              >
                Open Full KYRO Assistant &rarr;
              </button>

              <span style={{ color: '#64748B' }}>57,654 Files Offline</span>
            </div>

            {/* Input Bar (Voice & Text) */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAsk();
              }}
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '4px 6px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <button
                type="button"
                onClick={toggleVoice}
                style={{
                  background: isListening ? '#EE5D50' : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  width: '34px',
                  height: '34px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: isListening ? '0 0 10px #EE5D50' : 'none',
                  transition: 'all 0.15s',
                }}
                title={isListening ? 'Stop listening' : 'Speak to KYRO'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>

              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask or speak to KYRO..."
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '12.5px',
                  outline: 'none',
                  padding: '6px 4px',
                }}
              />

              <button
                type="submit"
                disabled={processing || !query.trim()}
                style={{
                  background: query.trim() ? '#00C0FA' : 'rgba(255, 255, 255, 0.1)',
                  color: query.trim() ? '#0B132B' : '#64748B',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: query.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '800',
                  transition: 'all 0.15s',
                }}
              >
                &rarr;
              </button>
            </form>
          </div>
        )}

        {/* ── Follow-Up Listening Floating Pill ── */}
        {isFollowUp && !isOpen && (
          <div
            onClick={() => setIsOpen(true)}
            style={{
              marginBottom: '10px',
              padding: '8px 14px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(0, 192, 250, 0.95), rgba(11, 19, 43, 0.95))',
              color: '#fff',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 8px 24px rgba(0, 192, 250, 0.4), 0 0 12px rgba(0, 192, 250, 0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              animation: 'pulse 1.5s infinite',
              border: '1px solid #00C0FA',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#00C0FA',
                boxShadow: '0 0 8px #00C0FA',
              }}
            />
            <span>🎙️ Follow-up Active · Speak anytime...</span>
          </div>
        )}

        {/* ── Proactive Thought Bubble ── */}
        {showProactiveBubble && !isOpen && !isFollowUp && (
          <div
            onClick={() => {
              setIsOpen(true);
              setShowProactiveBubble(false);
              if (proactiveBriefing?.answer) {
                setLastResponse({
                  answer: proactiveBriefing.answer,
                  voiceSummary: proactiveBriefing.voiceSummary,
                  sources: [],
                  latencyMs: 14,
                  quickActions: proactiveBriefing.quickActions || [],
                });
                if (proactiveBriefing.voiceSummary) {
                  speakText(proactiveBriefing.voiceSummary);
                }
              }
            }}
            style={{
              marginBottom: '12px',
              maxWidth: '310px',
              padding: '12px 14px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0B132B 0%, #1C2541 100%)',
              color: '#fff',
              fontSize: '12.5px',
              lineHeight: '1.45',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.5), 0 0 16px rgba(0, 192, 250, 0.35)',
              border: '1px solid rgba(0, 192, 250, 0.5)',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>⚡</span>
                <span
                  style={{
                    fontWeight: '800',
                    color: '#00C0FA',
                    fontSize: '10.5px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  KYRO Proactive Brief
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProactiveBubble(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94A3B8',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: '1',
                }}
                title="Dismiss"
              >
                ×
              </button>
            </div>
            <div style={{ color: '#E2E8F0', fontWeight: '500' }}>
              {user ? (
                <>
                  {proactiveBriefing?.greeting || 'Hello'}! You have{' '}
                  <strong style={{ color: '#00C0FA' }}>
                    {proactiveBriefing?.stats?.mappedFlowsCount ?? 0} mapped email flows
                  </strong>{' '}
                  ready for review today.
                </>
              ) : (
                <>
                  👋 Welcome! I am <strong style={{ color: '#00C0FA' }}>KYRO</strong>. Speak to log in, ask about PX-Reports, or ask for help!
                </>
              )}
            </div>
            <div
              style={{
                marginTop: '8px',
                fontSize: '11px',
                color: '#94A3B8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ color: '#00C0FA', fontWeight: '600' }}>Click to hear voice brief &rarr;</span>
              <span style={{ fontSize: '10px', color: '#64748B' }}>100% Offline AI</span>
            </div>
          </div>
        )}

        {/* ── Main Glowing Floating Orb Button ── */}
        <button
          onClick={toggleVoice}
          style={{
            position: 'relative',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: isListening
              ? 'radial-gradient(circle, #EE5D50 0%, #B91C1C 100%)'
              : 'radial-gradient(circle, #00C0FA 0%, #0B132B 85%)',
            border: isListening ? '2px solid #FECACA' : '2px solid rgba(0, 192, 250, 0.8)',
            boxShadow: isListening
              ? '0 0 24px rgba(238, 93, 80, 0.8), 0 8px 24px rgba(0,0,0,0.4)'
              : '0 0 24px rgba(0, 192, 250, 0.5), 0 8px 24px rgba(0,0,0,0.4)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            outline: 'none',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isListening ? 'scale(1.08)' : 'scale(1)',
          }}
          title={isListening ? 'Listening... click to stop' : 'Ask KYRO (Voice or Text)'}
        >
          {/* Pulsing ring animation when listening */}
          {isListening && (
            <span
              style={{
                position: 'absolute',
                top: '-6px',
                left: '-6px',
                right: '-6px',
                bottom: '-6px',
                borderRadius: '50%',
                border: '2px solid #EE5D50',
                animation: 'pulse 1.2s infinite',
                opacity: 0.8,
              }}
            />
          )}

          {/* Icon */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isListening ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="4" />
                <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
              </svg>
            )}
          </div>

          {/* Status Dot */}
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isListening ? '#EE5D50' : '#05CD99',
              border: '2px solid #0B132B',
              boxShadow: isListening ? '0 0 8px #EE5D50' : '0 0 8px #05CD99',
            }}
          />
        </button>
      </div>
    </>
  );
}
